import React from 'react';
import { Box, Chip, Tooltip, Typography, useTheme } from '@mui/material';
import { getDocTypeColor, getCitationSectionTitle } from '../../../../utils/utils';

/**
 * Compact document chip for contract chat timeline display
 * Shows doc_type tag (colored) and truncated title
 */
const ContractDocumentChip = ({ document, compact = true, maxTitleLength = 35 }) => {
  const theme = useTheme();
  const { metadata } = document || {};

  if (!metadata) return null;

  const docTypeColor = getDocTypeColor(metadata?.doc_type);
  const title = metadata?.doc_type === 'case-law'
    ? (metadata?.case_number || metadata?.subject_matter || 'Case')
    : (metadata?.title || 'Document');

  const displayTitle = compact && title.length > maxTitleLength
    ? `${title.substring(0, maxTitleLength)}...`
    : title;

  const docTypeAbbrev = metadata?.doc_type
    ? metadata.doc_type.substring(0, 3).toUpperCase()
    : 'DOC';

  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 280 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {title}
          </Typography>
          {metadata?.section_title && (
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
              {getCitationSectionTitle(metadata.section_title)}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
      enterDelay={300}
    >
      <Chip
        size="small"
        sx={{
          height: 24,
          maxWidth: compact ? 160 : 'none',
          cursor: 'default',
          backgroundColor: `${docTypeColor}10`,
          border: `1px solid ${docTypeColor}25`,
          borderRadius: '6px',
          transition: 'all 0.15s ease',
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0,
          },
        }}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: docTypeColor,
                color: '#ffffff',
                fontSize: '0.55rem',
                fontWeight: 700,
                px: 0.5,
                py: 0.1,
                borderRadius: '3px',
                textTransform: 'uppercase',
                letterSpacing: '0.2px',
                minWidth: 'fit-content',
                flexShrink: 0,
                lineHeight: 1.4,
              }}
            >
              {docTypeAbbrev}
            </Box>

            <Typography
              component="span"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                color: theme.palette.text.primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
              }}
            >
              {displayTitle}
            </Typography>
          </Box>
        }
      />
    </Tooltip>
  );
};

export default ContractDocumentChip;
